/**
 * Retention section - concept-level.
 *
 * Layout (top → bottom):
 *   1. Memory health hero  - large %, plain-English explainer
 *   2. Mastery distribution - donut on the left, labeled legend rows on the
 *      right. Each legend row is unambiguous: color dot + bucket name +
 *      count (with "concepts" qualifier) + percentage of total.
 *   3. Concepts to revisit - top-N weakest individual concepts with name,
 *      source lesson, last-reviewed timestamp, and retention bar.
 *
 * Every number on this card comes directly from `MyProgressResponse`
 * fields the backend computes - no synthetic data.
 */

import { Clock4 } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useChartColors } from '@/hooks/ui/useChartColors';
import { Card, ProgressBar, Skeleton } from '@ai-tutor/ui';
import type { MyProgressResponse } from '@/lib/api-client';
import { parseUtcDate } from '@/lib/datetime';

interface MindspaceSectionProps {
  data: MyProgressResponse | undefined;
  isLoading: boolean;
}

// Colors come from the theme-aware series palette (useChartColors), so the
// donut + legend follow dark's lavender-forward tokens instead of a baked-in
// indigo. `colorKey` indexes ChartColors['series'].
// Ordinal ramp: Strong = most prominent step (s1) → Weak = most muted (s4).
const DISTRIBUTION_BUCKETS = [
  { key: 'strong' as const, label: 'Strong', colorKey: 's1' as const },
  { key: 'familiar' as const, label: 'Familiar', colorKey: 's2' as const },
  { key: 'learning' as const, label: 'Learning', colorKey: 's3' as const },
  { key: 'weak' as const, label: 'Weak', colorKey: 's4' as const },
];

// Per-concept retention strength, on the same single-hue ramp as the donut
// (indigo in light / lavender in dark) so the whole section stays cohesive.
//
// `bar` and `text` step in the same direction but NOT over the same range, and
// they cannot: the bar is a graphic, so it only owes 3:1, while the percentage
// beside it is text and owes 4.5:1. Measured against the card in light mode,
// the ramp's lighter steps are nowhere near that - brand-400 is 2.12:1 and
// brand-500 is 3.04:1 - so the text ramp lives in 700/800/900, which run
// 6.32 / 8.61 / 11.27:1. The bars keep the light steps; they are the thing
// carrying the ordinal reading visually, and they are allowed to.
//
// This is the trap in globals.css's "400-500 icons and secondary text" note:
// it is safe for icons at 3:1 and not for the text half of the same sentence.
type Weak = MyProgressResponse['weakest_concepts'][number];

/** A weak concept's score and what it measures: predicted recall, or - for
 *  one often answered wrong but never reviewed - its quiz accuracy. */
export function weakScore(c: Weak): { pct: number; measure: string } {
  if (c.reason === 'quiz') {
    return {
      pct: Math.round((c.quiz_accuracy ?? 0) * 100),
      measure: 'right in quizzes',
    };
  }
  return { pct: Math.round((c.retention ?? 0) * 100), measure: 'recall' };
}

function retentionTone(pct: number): { bar: string; text: string } {
  if (pct >= 80)
    return {
      bar: 'bg-brand-600 dark:bg-primary',
      text: 'text-brand-900 dark:text-primary',
    };
  if (pct >= 50)
    return {
      bar: 'bg-brand-400 dark:bg-primary/70',
      text: 'text-brand-800 dark:text-primary/90',
    };
  return {
    bar: 'bg-brand-300 dark:bg-primary/40',
    text: 'text-brand-700 dark:text-muted-foreground',
  };
}

function lastReviewedLabel(iso: string | null): string {
  if (!iso) return 'never reviewed';
  const now = Date.now();
  const then = parseUtcDate(iso).getTime();
  const days = Math.max(0, Math.round((now - then) / (1000 * 60 * 60 * 24)));
  if (days === 0) return 'reviewed today';
  if (days === 1) return 'reviewed yesterday';
  if (days < 7) return `reviewed ${days}d ago`;
  if (days < 30) return `reviewed ${Math.round(days / 7)}w ago`;
  return `reviewed ${Math.round(days / 30)}mo ago`;
}

/**
 * Loading state for the Retention card: hero, distribution row, concepts list.
 *
 * The three dividers (`border-b border-border`) are reproduced because they are
 * what gives this card its internal rhythm - a skeleton of three undivided
 * blocks reads as a different component, not as this one loading.
 *
 * The donut is a plain shimmering circle rather than an inert Recharts ring.
 * Mounting the chart here would pull in `useChartColors` and a
 * `ResponsiveContainer` measure pass to draw a grey circle, and would make the
 * loading state depend on the charting library rendering correctly.
 */
function MindspaceSkeleton() {
  return (
    <Card elevation='none' className='p-5'>
      {/* Memory Health hero */}
      <div className='flex flex-col gap-4 border-b border-border pb-5 sm:flex-row sm:items-center sm:justify-between'>
        <div className='space-y-2'>
          <Skeleton className='h-4 w-32' />
          <Skeleton className='h-3 w-80 max-w-full' />
        </div>
        <div className='flex items-baseline gap-3'>
          <Skeleton className='h-10 w-20' />
          <div className='space-y-1'>
            <Skeleton className='h-3 w-20' />
            <Skeleton className='h-3 w-20' />
          </div>
        </div>
      </div>

      {/* Distribution row - donut + labelled legend */}
      <div className='border-b border-border py-5'>
        <div className='mb-4 flex items-center justify-between'>
          <Skeleton className='h-4 w-32' />
          <Skeleton className='h-3 w-20' />
        </div>
        <div className='grid grid-cols-1 items-center gap-6 sm:grid-cols-[160px_1fr]'>
          <Skeleton className='mx-auto h-40 w-40 rounded-full' />
          <div className='space-y-2.5'>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className='flex items-center gap-3'>
                <Skeleton className='h-2.5 w-2.5 rounded-sm' />
                <Skeleton className='h-3 w-16' />
                <Skeleton className='h-1.5 flex-1 rounded-full' />
                <Skeleton className='h-3 w-16' />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Concepts to revisit */}
      <div className='pt-5'>
        <div className='mb-3 flex items-center justify-between'>
          <Skeleton className='h-4 w-32' />
          <Skeleton className='h-3 w-24' />
        </div>
        <ul className='space-y-2.5'>
          {Array.from({ length: 6 }).map((_, i) => (
            <li key={i} className='flex items-center gap-3 px-2 py-1.5'>
              <div className='min-w-0 flex-1 space-y-1.5'>
                <Skeleton className='h-4 w-40 max-w-full' />
                <Skeleton className='h-3 w-56 max-w-full' />
              </div>
              <Skeleton className='h-1.5 w-24 rounded-full' />
              <Skeleton className='h-4 w-10' />
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

/** Concepts studied without a review, and every concept worked on (older
 *  responses carry only the revised count). */
function evidenceCounts(data: MyProgressResponse) {
  return {
    studied: data.mastery_distribution.studied ?? 0,
    workedOn:
      data.concepts_worked_on_in_period ?? data.concepts_revised_in_period,
  };
}

/** Concepts read or quizzed this period but never reviewed: no retention
 *  yet, so a row of their own rather than a slice of the donut. */
function StudiedRow({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <div className='flex items-center gap-3 border-t border-border pt-2.5 text-sm'>
      <span
        aria-hidden
        className='h-2.5 w-2.5 shrink-0 rounded-sm border border-dashed border-muted-foreground'
      />
      <span className='min-w-0 flex-1 text-muted-foreground'>
        Studied, not yet tested
      </span>
      <span className='shrink-0 text-xs font-semibold tabular-nums text-foreground'>
        {count}
      </span>
    </div>
  );
}

export function MindspaceSection({ data, isLoading }: MindspaceSectionProps) {
  const chartColors = useChartColors();
  if (isLoading) return <MindspaceSkeleton />;
  if (!data) return null;

  const memoryHealthPct = Math.round(data.memory_health * 100);
  const { studied, workedOn } = evidenceCounts(data);
  const distributionTotal =
    data.mastery_distribution.strong +
    data.mastery_distribution.familiar +
    data.mastery_distribution.learning +
    data.mastery_distribution.weak;

  // Donut data - fall back to a single inert ring when no concepts are
  // reviewed so the visualization doesn't collapse.
  const donutData =
    distributionTotal > 0
      ? DISTRIBUTION_BUCKETS.map(b => ({
          name: b.label,
          value: data.mastery_distribution[b.key],
          color: chartColors.series[b.colorKey],
        })).filter(d => d.value > 0)
      : [{ name: 'Empty', value: 1, color: chartColors.grid }];

  return (
    <Card elevation='none' className='p-5'>
      {/* Memory Health hero */}
      <div className='flex flex-col gap-4 border-b border-border pb-5 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h3 className='text-sm font-semibold text-foreground'>
            Memory health
          </h3>
          {distributionTotal > 0 ? (
            <p className='mt-2 text-xs text-muted-foreground'>
              Average retention across the{' '}
              <span className='font-medium text-muted-foreground'>
                {distributionTotal} concepts
              </span>{' '}
              you reviewed this period.
            </p>
          ) : (
            <p className='mt-2 text-xs text-muted-foreground'>
              Grade flashcards or attempt quizzes to start tracking retention.
            </p>
          )}
        </div>
        {distributionTotal > 0 && (
          <div className='flex items-baseline gap-3'>
            <span className='text-4xl font-bold tabular-nums text-foreground'>
              {memoryHealthPct}
              <span className='ml-0.5 text-xl font-normal text-muted-foreground'>
                %
              </span>
            </span>
            <div className='flex flex-col text-xs text-muted-foreground'>
              <span>
                <span className='font-semibold tabular-nums text-foreground'>
                  {data.concepts_mastered}
                </span>{' '}
                mastered
              </span>
              <span>
                <span className='font-semibold tabular-nums text-foreground'>
                  {workedOn}
                </span>{' '}
                worked on
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Mastery distribution - donut + labeled legend */}
      <div className='border-b border-border py-5'>
        <div className='mb-4 flex items-center justify-between'>
          <h4 className='text-sm font-semibold text-foreground'>
            Mastery distribution
          </h4>
          <span className='text-xs text-muted-foreground'>
            {distributionTotal} reviewed
          </span>
        </div>

        {distributionTotal === 0 ? (
          <>
            <p className='py-2 text-xs text-muted-foreground'>
              Grade flashcards or attempt quizzes to populate the buckets.
            </p>
            <StudiedRow count={studied} />
          </>
        ) : (
          <div className='grid grid-cols-1 items-center gap-6 sm:grid-cols-[160px_1fr]'>
            {/* Donut */}
            <div className='relative mx-auto h-40 w-40'>
              <ResponsiveContainer width='100%' height='100%'>
                <PieChart>
                  <Pie
                    data={donutData}
                    cx='50%'
                    cy='50%'
                    innerRadius={52}
                    outerRadius={76}
                    paddingAngle={2}
                    dataKey='value'
                    isAnimationActive={false}
                  >
                    {donutData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} stroke='none' />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center'>
                <span className='text-2xl font-bold tabular-nums text-foreground'>
                  {data.concepts_mastered}
                </span>
                <span className='text-xs text-muted-foreground'>mastered</span>
              </div>
            </div>

            {/* Legend rows - labeled count + percentage, no ambiguity */}
            <div className='space-y-2.5'>
              {DISTRIBUTION_BUCKETS.map(b => {
                const count = data.mastery_distribution[b.key];
                const pct = Math.round((count / distributionTotal) * 100);
                return (
                  <div key={b.key} className='flex items-center gap-3 text-sm'>
                    <span
                      className='h-2.5 w-2.5 shrink-0 rounded-sm'
                      style={{
                        backgroundColor: chartColors.series[b.colorKey],
                      }}
                    />
                    <span className='w-20 shrink-0 text-muted-foreground'>
                      {b.label}
                    </span>
                    <ProgressBar
                      value={pct}
                      size='sm'
                      className='min-w-0 flex-1'
                      fillColor={chartColors.series[b.colorKey]}
                      ariaLabel={b.label}
                      ariaValueText={`${b.label}: ${count} concepts, ${pct}% of total`}
                    />
                    <span className='shrink-0 text-xs text-muted-foreground'>
                      <span className='font-semibold tabular-nums text-foreground'>
                        {count}
                      </span>{' '}
                      <span className='text-muted-foreground'>·</span>{' '}
                      <span className='tabular-nums'>{pct}%</span>
                    </span>
                  </div>
                );
              })}
              <StudiedRow count={studied} />
            </div>
          </div>
        )}
      </div>

      {/* Concepts to revisit - individual concepts, not subjects */}
      <div className='pt-5'>
        <div className='mb-3 flex items-start justify-between'>
          <div>
            <h4 className='text-sm font-semibold text-foreground'>
              Concepts to revisit
            </h4>
            {/* The percentage is FSRS predicted recall - the model's estimate of
                your chance of remembering the concept right now (lower = more
                due). Labeled so it isn't mistaken for a quiz score. */}
            <p className='mt-0.5 text-xs text-muted-foreground'>
              % = predicted recall, or share answered right in quizzes
            </p>
          </div>
          {data.weakest_concepts && data.weakest_concepts.length > 0 && (
            <span className='text-xs text-muted-foreground'>
              {data.weakest_concepts.length} weakest
            </span>
          )}
        </div>

        {!data.weakest_concepts || data.weakest_concepts.length === 0 ? (
          <p className='py-2 text-xs text-muted-foreground'>
            Nothing in the weak zone right now - your concepts are holding up.
          </p>
        ) : (
          <ul className='space-y-2.5'>
            {data.weakest_concepts.slice(0, 10).map(c => {
              const { pct, measure } = weakScore(c);
              const tone = retentionTone(pct);
              return (
                // Below `sm` the row is a grid: the full name with its
                // percentage, the lesson and last review under it, then the
                // bar across the width. One line (`sm` and up) cut a phone's
                // names to "Eigenvalues & eig…" and wrapped the date in two.
                // `contents` lifts the two wrappers' children into that grid
                // so both shapes come from the same markup.
                <li
                  key={c.concept_id}
                  className='group grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-1 rounded-lg border border-transparent px-2 py-1.5 transition-colors hover:border-gray-200 hover:bg-accent/60 dark:hover:border-border sm:flex sm:items-center'
                >
                  <div className='contents sm:block sm:min-w-0 sm:flex-1'>
                    <div className='col-start-1 row-start-1 text-sm font-medium text-foreground sm:truncate'>
                      {c.name}
                    </div>
                    <div className='col-span-2 row-start-2 mt-0.5 flex min-w-0 items-center gap-2 text-xs text-muted-foreground'>
                      {c.lesson_title && (
                        <span className='truncate'>{c.lesson_title}</span>
                      )}
                      <span className='inline-flex shrink-0 items-center gap-1 whitespace-nowrap'>
                        <Clock4 className='h-3 w-3' />
                        {lastReviewedLabel(c.last_reviewed_at)}
                      </span>
                    </div>
                  </div>
                  <div className='contents sm:flex sm:items-center sm:gap-3'>
                    <ProgressBar
                      value={pct}
                      size='sm'
                      className='col-span-2 row-start-3 mt-1 sm:mt-0 sm:w-24'
                      fillClassName={tone.bar}
                      ariaLabel={`${c.name} ${measure}`}
                      ariaValueText={`${c.name}: ${pct}% ${measure}`}
                    />
                    <span
                      className={`col-start-2 row-start-1 text-right text-sm font-semibold tabular-nums sm:w-10 ${tone.text}`}
                    >
                      {pct}%
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}
